// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import XngZone from "@conklin/App/Components/Common/XngZone";
import { setFilterDrawerIsOpen } from "@conklin/App/Handlers/XNG/XListFilter";
import { CustomAppState } from "@conklin/App/Store/Reducers";
import Button, { ButtonPresentationProps } from "@conklin/MobiusOverrides/Button/Button";
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import clearAllProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/ClearAllProductFilters";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable from "@insite/mobius/Clickable";
import Drawer, { DrawerPresentationProps } from "@insite/mobius/Drawer";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";

const mapStateToProps = (state: CustomAppState) => ({
    view: state.pages.productList.view,
    // XNG-change
    isTabletScreen: state.xCommon.isTabletScreen,
    filterDrawerIsOpen: state.xProductList.filterDrawerIsOpen,
});

// XNG-Changes: add mapDispatchToProps
const mapDispatchToProps = {
    clearAllProductFilters,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductListColumnsStyles {
    container?: GridContainerProps;
    leftColumnGridItem?: GridItemProps;
    leftColumnTableViewGridItem?: GridItemProps;
    rightColumnGridItem?: GridItemProps;
    rightColumnTableViewGridItem?: GridItemProps;
    // XNG-Change
    filterWrapper?: InjectableCss;
    buttonsWrapper?: InjectableCss;
    cancelBtn?: ButtonPresentationProps;
    applyBtn?: ButtonPresentationProps;
    drawer?: DrawerPresentationProps;
    titleContainer?: TypographyPresentationProps;
    title?: TypographyPresentationProps;
    controlsWrapper?: InjectableCss;
    extraCards?: InjectableCss;
    closeBtn?: LinkPresentationProps;
}

export const columnsStyles: ProductListColumnsStyles = {
    container: {
        gap: 20,
    },
    leftColumnGridItem: {
        width: [12, 12, 3, 3, 3],
    },
    leftColumnTableViewGridItem: {
        width: [12, 12, 0, 0, 0],
    },
    rightColumnGridItem: {
        width: [12, 12, 9, 9, 9],
    },
    rightColumnTableViewGridItem: {
        width: 12,
    },
};

const styles = columnsStyles;

const ProductListColumns: FC<Props> = ({ id, view, filterDrawerIsOpen, isTabletScreen, clearAllProductFilters }) => {
    // XNG-Change: from check view === table to tablet
    const dispatch = useAppDispatch();
    // <XNG-Changes>
    const handleCloseFilterDrawer = () => {
        dispatch({
            type: "xProductList/FilterDrawer/SetFilterDrawerIsOpen",
            filterDrawerIsOpen: false,
        });
    };
    const handleClearFilterDrawerValues = () => {
        clearAllProductFilters();
        handleCloseFilterDrawer();
    };
    // </XNG-Changes>

    // XNG-Change: Restructure
    return (
        <StyledWrapper
            {...styles.container}
            // XNG-Changes: add id for back to top
            id="productList-controlsContainer"
        >
            {!isTabletScreen && (
                <StyledWrapper {...styles.leftColumnGridItem}>
                    <Zone contentId={id} zoneName="Content00" />
                </StyledWrapper>
            )}

            <StyledWrapper {...styles.extraCards}>
                <XngZone contentId={id} zoneName="Content02" hint="Extra Card" />
            </StyledWrapper>

            <StyledWrapper {...styles.rightColumnGridItem}>
                <Zone contentId={id} zoneName="Content01" />
            </StyledWrapper>
            {/* XNG-Changes: add drawer for tablet & mobile screens */}
            <Drawer
                isOpen={filterDrawerIsOpen}
                handleClose={handleCloseFilterDrawer}
                position="left"
                draggable={false}
                contentLabel="filter drawer"
                headline={
                    <StyledWrapper {...styles.titleContainer}>
                        <Typography {...styles.title}>{translate("Refine Results")}</Typography>
                        <Link {...styles.closeBtn} onClick={() => handleCloseFilterDrawer()}></Link>
                    </StyledWrapper>
                }
                {...styles.drawer}
            >
                <StyledWrapper {...styles.filterWrapper}>
                    <StyledWrapper {...styles.controlsWrapper}>
                        {createWidgetElement("ProductList/ProductListSortSelect", { fields: {} })}
                        {createWidgetElement("ProductList/ProductListPagination", {
                            fields: { showPagesNumber: false, showResultsPerPage: true },
                        })}
                    </StyledWrapper>
                    <Zone contentId={id} zoneName="Content00" />
                    <StyledWrapper {...styles.buttonsWrapper}>
                        <Button {...styles.cancelBtn} onClick={handleClearFilterDrawerValues} aria-label="Cancel">
                            {translate("Clear Filters")}
                        </Button>
                        <Button {...styles.applyBtn} onClick={handleCloseFilterDrawer} aria-label="Apply Filter">
                            {translate("Apply Filters")}
                        </Button>
                    </StyledWrapper>
                </StyledWrapper>
            </Drawer>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ProductListColumns),
    definition: {
        group: "Product List",
        displayName: "Columns",
        // XNG-changes: add CategoryDetailsPage
        allowedContexts: ["ProductListPage", "CategoryDetailsPage"],
    },
};

export default widgetModule;
